/**
 * Places Autocomplete via Places API (New) REST.
 *
 * Do NOT use `google.maps.places.Autocomplete` / AutocompleteService — those hit
 * AutocompletionService.GetPredictions and throw LegacyApiNotActivatedMapError
 * when only Places API (New) is enabled on the Cloud project.
 *
 * AutocompleteSuggestion in the JS Maps library has also been observed to fall
 * through to that same legacy endpoint, so this component talks to
 * places.googleapis.com directly.
 */

import React, { useEffect, useRef, useState } from "react";

export interface PlacesPick {
  placeId: string;
  name: string;
  lat?: number;
  lng?: number;
}

interface Props {
  value: string;
  onChange: (text: string) => void;
  onPick: (place: PlacesPick) => void;
  /** Maps / Places API key (Places API New must be enabled on the key). */
  apiKey: string;
  placeholder?: string;
  /** ISO country codes, lower-case — e.g. ["ke"]. */
  regionCodes?: string[];
  /** Fetch geometry for map pin drops. Default false (name + placeId only). */
  wantLocation?: boolean;
  inputStyle?: React.CSSProperties;
  className?: string;
  disabled?: boolean;
}

type SuggestionRow = {
  id: string;
  label: string;
};

type AutocompleteJson = {
  suggestions?: Array<{
    placePrediction?: {
      placeId?: string;
      text?: { text?: string };
      structuredFormat?: { mainText?: { text?: string } };
    };
  }>;
  error?: { message?: string; status?: string };
};

type PlaceDetailsJson = {
  id?: string;
  displayName?: { text?: string };
  location?: { latitude?: number; longitude?: number };
  error?: { message?: string };
};

function placesErrorMessage(status: number, detail?: string): string {
  if (status === 403) {
    return "Search is not authorised yet. Enable Places API (New) for this browser key, then try again.";
  }
  return detail || "Places search failed. Type the name and continue, or try again.";
}

export default function PlacesNewAutocomplete({
  value,
  onChange,
  onPick,
  apiKey,
  placeholder = "Search…",
  regionCodes = ["ke"],
  wantLocation = false,
  inputStyle,
  className,
  disabled,
}: Props) {
  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const sessionRef = useRef(`sess_${Math.random().toString(36).slice(2, 12)}`);

  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!apiKey || input.trim().length < 2) {
      setRows([]);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
        },
        body: JSON.stringify({
          input: input.trim(),
          includedRegionCodes: regionCodes,
          sessionToken: sessionRef.current,
        }),
      });
      const data = (await res.json()) as AutocompleteJson;
      if (!res.ok) {
        throw new Error(placesErrorMessage(res.status, data.error?.message));
      }
      const next: SuggestionRow[] = [];
      for (const s of data.suggestions || []) {
        const pred = s.placePrediction;
        if (!pred?.placeId) continue;
        const label = (
          pred.text?.text
          || pred.structuredFormat?.mainText?.text
          || ""
        ).trim();
        if (!label) continue;
        next.push({ id: pred.placeId, label });
      }
      setRows(next.slice(0, 8));
      setOpen(next.length > 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Places search failed. Type the name and continue, or try again.");
      setRows([]);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const onInput = (text: string) => {
    onChange(text);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void fetchSuggestions(text), 220);
  };

  const pick = async (row: SuggestionRow) => {
    setOpen(false);
    setRows([]);
    onChange(row.label);

    if (!wantLocation) {
      onPick({ placeId: row.id, name: row.label });
      sessionRef.current = `sess_${Math.random().toString(36).slice(2, 12)}`;
      return;
    }

    try {
      const res = await fetch(
        `https://places.googleapis.com/v1/places/${encodeURIComponent(row.id)}`,
        {
          method: "GET",
          headers: {
            "X-Goog-Api-Key": apiKey,
            "X-Goog-FieldMask": "id,displayName,location",
          },
        },
      );
      const data = (await res.json()) as PlaceDetailsJson;
      if (!res.ok) throw new Error(placesErrorMessage(res.status, data.error?.message));
      const lat = data.location?.latitude;
      const lng = data.location?.longitude;
      onPick({
        placeId: data.id || row.id,
        name: data.displayName?.text || row.label,
        ...(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {}),
      });
    } catch (e) {
      onPick({ placeId: row.id, name: row.label });
      setErr(e instanceof Error ? e.message : "Could not load place details. Click the map to place the pin instead.");
    } finally {
      sessionRef.current = `sess_${Math.random().toString(36).slice(2, 12)}`;
    }
  };

  if (!apiKey) {
    return (
      <input
        className={className}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
      />
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} data-places-new="rest">
      <input
        className={className}
        value={value}
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => { if (rows.length) setOpen(true); }}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {busy && (
        <div style={{ position: "absolute", right: 10, top: 12, fontSize: 11, color: "#94a3b8" }}>…</div>
      )}
      {open && rows.length > 0 && (
        <ul
          style={{
            position: "absolute",
            zIndex: 40,
            left: 0,
            right: 0,
            top: "100%",
            margin: "4px 0 0",
            padding: 0,
            listStyle: "none",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(15,23,42,.12)",
            maxHeight: 220,
            overflow: "auto",
          }}
        >
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void pick(r)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  padding: "10px 12px",
                  fontSize: 13.5,
                  color: "#0f172a",
                  cursor: "pointer",
                }}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && (
        <div
          role="status"
          style={{
            fontSize: 12,
            color: "#92400e",
            marginTop: 6,
            padding: "7px 9px",
            borderRadius: 7,
            background: "#fffbeb",
            border: "1px solid #fde68a",
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
}
